import { useEffect, useMemo, useRef, useState } from 'react';
import { api, API_ENDPOINTS } from '../../lib/api';
import { captureError } from '../../lib/errorHandler';
import { useChargePointStatus } from '../../hooks/useChargePointStatus';
import {
  mapTariffsToSchedule,
  isWindowActiveNow,
  type TariffScheduleEntry,
} from '../../utils/tariffSchedule';
import { ConnectorCard, isActionableConnector, type LiveConnector } from './ConnectorCard';
import { ConnectorRow } from './ConnectorRow';
import type { DetailedChargePoint } from '../../types';

/**
 * Live connectors for a single charge point: a charger header, one collapsed row
 * per gun, and the expanded detail for whichever gun is open.
 *
 * Owns the two fetches a charge point needs — an initial status snapshot and the
 * per-EVSE tariff schedules — plus the 5s status poll. It exists as a component
 * rather than a hook because a location can hold several charge points and hooks
 * cannot be called in a loop; each charge point gets its own instance and its own
 * poll. In practice that is one instance for 87% of locations and never more than
 * four, so no location-level status endpoint is warranted.
 *
 * Which gun is expanded is owned by the parent, because the accordion spans the
 * whole location rather than one charger.
 */

// Response of /charge-points/:id/status — the initial snapshot. Distinct from the
// live endpoint the poll uses, which also carries `source` and `currentSoc`.
interface ChargePointStatusResponse {
  id: string;
  status: string;
  isOnline: boolean;
  evses: {
    id: string;
    connectorId: string;
    status: string;
  }[];
  timestamp: string;
}

interface TariffPriceComponent {
  type: 'Energy' | 'Flat' | 'ParkingTime' | 'Time';
  price: number;
  stepSize: number;
  vat?: number;
}

interface TariffElementResponse {
  id: string;
  priceComponents: TariffPriceComponent[];
}

interface TariffResponse {
  id: string;
  tariffElements: TariffElementResponse[];
  currency: string;
}

export interface ChargePointStatusInfo {
  isOnline: boolean | undefined;
  loaded: boolean;
  connectorIds: string[];
  /** First gun here worth acting on, for the parent's auto-expand. */
  firstActionableId: string | null;
  /** Any gun here reporting available, for the parent's ordering of chargers. */
  hasAvailable: boolean;
  /** Any gun here plugged in and waiting to be started. */
  hasPreparing: boolean;
  /** Gun number (as printed on the sticker) -> connector id. */
  connectorIdsByGun: Record<string, string>;
  /** Snapshot /status failed and live poll has not landed yet. */
  snapshotFailed: boolean;
}

interface ChargePointConnectorsProps {
  chargePoint: DetailedChargePoint;
  /** Gate every fetch and the poll on the containing sheet being open. */
  enabled: boolean;
  /** Show the charger name and specs above the guns. */
  showHeader?: boolean;
  /** Id of the location-wide expanded gun; null when this charger has none open. */
  expandedConnectorId: string | null;
  /**
   * Whether the open gun was opened by a tap. Auto-expansion must not scroll:
   * the sheet should open at the top, and scrolling to the gun clips the charger
   * header sitting above it.
   */
  expandedByUser?: boolean;
  onToggleConnector: (connectorId: string) => void;
  onStartCharging?: (chargePointId: string, connectorId: string) => void;
  /** True while a start-charging request is in flight. Disables the Start button. */
  isStartingCharge?: boolean;
  /**
   * Reports connectivity and connector identity upward so the parent can drive
   * the accordion and render its own chrome without duplicating the fetch.
   * Must be referentially stable.
   */
  onStatusResolved?: (info: ChargePointStatusInfo) => void;
}

/**
 * A gun in `preparing` leads: it means a cable is physically in a car and a
 * Start Charging button is waiting, so whoever is looking at the app is most
 * likely the person standing at it. A free gun is useful to someone; a
 * preparing gun is urgent to someone, and is not available to anyone else.
 */
function gunPriority(connector: LiveConnector): number {
  // Out of service sinks below every usable state, whatever it reports.
  if (connector.active === false) return 3;
  if (connector.status === 'preparing') return 0;
  if (connector.status === 'available') return 1;
  return 2;
}

/** The window in force right now, or the only entry when there are no windows. */
function activeEntry(schedule: TariffScheduleEntry[]): TariffScheduleEntry | undefined {
  if (schedule.length === 0) return undefined;
  const isTou = schedule.some((e) => e.startTime && e.endTime);
  if (!isTou) return schedule[0];
  return schedule.find((entry) => isWindowActiveNow(entry));
}

export function ChargePointConnectors({
  chargePoint,
  enabled,
  showHeader = false,
  expandedConnectorId,
  expandedByUser = false,
  onToggleConnector,
  onStartCharging,
  isStartingCharge = false,
  onStatusResolved,
}: ChargePointConnectorsProps) {
  const { isOnline: liveIsOnline, evseStatuses } = useChargePointStatus({
    chargePointId: chargePoint.id,
    enabled,
  });

  const [apiStatus, setApiStatus] = useState<{
    isOnline: boolean;
    evses: Map<string, string>; // evseId -> status
  } | null>(null);
  const [snapshotFailed, setSnapshotFailed] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // evseId -> full tariff schedule (all time-of-use windows, not just the first)
  const [tariffSchedules, setTariffSchedules] = useState<Map<string, TariffScheduleEntry[]>>(new Map());

  // Initial status snapshot — renders before the first poll returns. On failure
  // we keep listening to the 5s live poll instead of pinning the skeleton.
  useEffect(() => {
    if (!enabled) {
      setApiStatus(null);
      setSnapshotFailed(false);
      setTariffSchedules(new Map());
      return;
    }
    let cancelled = false;
    api
      .get<ChargePointStatusResponse>(API_ENDPOINTS.CHARGE_POINTS.STATUS(chargePoint.id))
      .then((response) => {
        if (cancelled) return;
        const evseMap = new Map<string, string>();
        response.data.evses.forEach((evse) => {
          evseMap.set(evse.id, evse.status);
        });
        setApiStatus({ isOnline: response.data.isOnline, evses: evseMap });
        setSnapshotFailed(false);
      })
      .catch((error) => {
        if (cancelled) return;
        captureError(error, 'ChargePointConnectors.fetchStatus');
        setSnapshotFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, chargePoint.id, retryNonce]);

  useEffect(() => {
    if (!enabled || (!apiStatus && evseStatuses.size === 0)) return;
    let cancelled = false;

    const fetchTariffs = async () => {
      const schedules = new Map<string, TariffScheduleEntry[]>();
      await Promise.all(
        chargePoint.connectors.map(async (connector) => {
          const evseId = connector.evseId || connector.id;
          try {
            const response = await api.get<TariffResponse[]>(API_ENDPOINTS.TARIFFS.RESOLVE(evseId));
            const schedule = mapTariffsToSchedule(response.data);
            if (schedule.length > 0) {
              schedules.set(evseId, schedule);
            }
          } catch {
            // Silent failure — tariff section won't render
          }
        })
      );
      if (!cancelled) setTariffSchedules(schedules);
    };

    fetchTariffs();
    return () => {
      cancelled = true;
    };
  }, [enabled, chargePoint.id, apiStatus, evseStatuses.size]);

  // Poll (live) > snapshot > unknown. Never falls back to the stale list payload.
  const isOnline = liveIsOnline ?? apiStatus?.isOnline;

  const connectors: LiveConnector[] = useMemo(() => {
    const hasLive = evseStatuses.size > 0;
    if (!apiStatus && !hasLive) return [];

    return chargePoint.connectors.map((connector) => {
      const evseId = connector.evseId || connector.id;
      const liveStatus = evseStatuses.get(evseId);
      const snapshotStatus = apiStatus?.evses.get(evseId);

      let effectiveStatus: string;
      let rawStatus: string | undefined;

      if (liveStatus?.status) {
        effectiveStatus = liveStatus.status;
        rawStatus = liveStatus.rawStatus;
      } else if (snapshotStatus) {
        effectiveStatus = snapshotStatus;
        rawStatus = snapshotStatus;
      } else {
        // EVSE absent from the status response — show unknown rather than guess
        effectiveStatus = 'unknown';
        rawStatus = undefined;
      }

      return {
        ...connector,
        status: effectiveStatus as LiveConnector['status'],
        rawStatus,
        currentSoc: liveStatus?.currentSoc ?? null,
      };
    });
  }, [chargePoint.connectors, evseStatuses, apiStatus]);

  // Available guns first — but latched at the first status rather than re-sorted
  // on every poll, so a row cannot move out from under the finger reaching for it.
  // Reopening the sheet recomputes it.
  const gunOrderRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (!enabled) gunOrderRef.current = null;
  }, [enabled]);

  const orderedConnectors = useMemo(() => {
    if (connectors.length === 0) return connectors;
    if (!gunOrderRef.current) {
      gunOrderRef.current = [...connectors]
        .sort((a, b) => gunPriority(a) - gunPriority(b))
        .map((c) => c.id);
    }
    const order = gunOrderRef.current;
    return [...connectors].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }, [connectors]);

  // Specs and price live on the header when every gun here shares them, which is
  // the norm: prod has no EVSE-level tariff assignment at all, only charge-point
  // level. When they do differ, each gun carries its own and the header stays quiet.
  const sharedSpecs = useMemo(() => {
    if (connectors.length === 0) return null;
    const { power, type } = connectors[0];
    return connectors.every((c) => c.power === power && c.type === type) ? { power, type } : null;
  }, [connectors]);

  // Power is deliberately absent from the header: charger names already carry it
  // (8 of 9 in prod read like "22kW SG"), so repeating it beside the name is noise.
  // Guns that disagree on power still show it on their own row.
  const sharedType = useMemo(() => {
    if (connectors.length === 0) return null;
    const { type } = connectors[0];
    return connectors.every((c) => c.type === type) ? type : null;
  }, [connectors]);

  const sharedSchedule = useMemo(() => {
    if (connectors.length === 0) return null;
    const first = tariffSchedules.get(connectors[0].evseId || connectors[0].id) ?? [];
    if (first.length === 0) return null;
    const key = JSON.stringify(first);
    const same = connectors.every(
      (c) => JSON.stringify(tariffSchedules.get(c.evseId || c.id) ?? []) === key
    );
    return same ? first : null;
  }, [connectors, tariffSchedules]);

  const headerPrice = sharedSchedule ? activeEntry(sharedSchedule) : undefined;

  const connectorIdsKey = orderedConnectors.map((c) => c.id).join(',');
  // Keyed on a string so the effect below compares by value; the object itself
  // would be a fresh reference on every render and re-fire it endlessly.
  const gunMapKey = orderedConnectors.map((c) => `${c.gunNumber}:${c.id}`).join(',');
  const connectorIdsByGun = useMemo(() => {
    const map: Record<string, string> = {};
    for (const connector of orderedConnectors) {
      map[String(connector.gunNumber)] = connector.id;
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gunMapKey]);
  // Nothing on an unreachable charger is actionable, whatever its guns last
  // reported — so it never wins the location's auto-expand.
  const firstActionableId =
    isOnline === false ? null : orderedConnectors.find(isActionableConnector)?.id ?? null;
  const hasAvailable = connectors.some((c) => c.active !== false && c.status === 'available');
  const hasPreparing = connectors.some((c) => c.active !== false && c.status === 'preparing');

  useEffect(() => {
    onStatusResolved?.({
      isOnline,
      loaded: !!apiStatus || evseStatuses.size > 0 || snapshotFailed,
      connectorIds: connectorIdsKey ? connectorIdsKey.split(',') : [],
      firstActionableId,
      hasAvailable,
      hasPreparing,
      connectorIdsByGun,
      snapshotFailed: snapshotFailed && evseStatuses.size === 0 && !apiStatus,
    });
  }, [
    onStatusResolved,
    isOnline,
    apiStatus,
    evseStatuses.size,
    snapshotFailed,
    connectorIdsKey,
    firstActionableId,
    hasAvailable,
    hasPreparing,
    connectorIdsByGun,
  ]);

  // Keep a tapped gun in view. `nearest` means no jump when it already is.
  const expandedRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!expandedByUser || !expandedConnectorId || !expandedRef.current) return;
    expandedRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [expandedByUser, expandedConnectorId]);

  const statusReady = !!apiStatus || evseStatuses.size > 0;

  if (enabled && !statusReady && snapshotFailed) {
    return (
      <div className="space-y-2 py-2">
        <p className="text-sm text-gray-500 dark:text-gray-400">Couldn&apos;t load charger status.</p>
        <button
          type="button"
          onClick={() => {
            setSnapshotFailed(false);
            setRetryNonce((n) => n + 1);
          }}
          className="text-sm font-medium text-[#8bc43e] hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  // Snapshot still in flight and live poll has not landed — hold the space.
  if (enabled && !statusReady) {
    return (
      <div className="space-y-2 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
        <div className="h-12 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  // Charge point with no EVSE rows — nothing to act on, so stay out of the way.
  if (connectors.length === 0) return null;

  return (
    <div className="space-y-2">
      {showHeader && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
            {chargePoint.name || 'Charge Point'}
          </h4>
          {sharedType && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{sharedType}</span>
          )}
          {headerPrice && (
            <span className="text-xs font-semibold text-[#8bc43e]">₹{headerPrice.price}/kWh</span>
          )}
          {isOnline !== undefined && (
            <span
              className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isOnline
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          )}
        </div>
      )}

      {orderedConnectors.map((connector) => {
        const expanded = connector.id === expandedConnectorId;
        return (
          <div key={connector.id} ref={expanded ? expandedRef : undefined}>
            <ConnectorRow
              connector={connector}
              expanded={expanded}
              onToggle={() => onToggleConnector(connector.id)}
              showSpecs={!sharedSpecs}
              isOnline={isOnline}
            />
            {expanded && (
              <ConnectorCard
                connector={connector}
                schedule={tariffSchedules.get(connector.evseId || connector.id) ?? []}
                isOnline={isOnline}
                showSpecs={!sharedSpecs}
                reportsPreparing={chargePoint.reportsPreparing}
                onStartCharging={() => onStartCharging?.(chargePoint.id, connector.id)}
                starting={isStartingCharge}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
